import { normaliseBrandSettings, normaliseBrandTitle } from '../../../shared/src/utils/branding';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { getLogoUrl } from '@/utils/logoUtils';

export interface Settings {
  agentPortalPrimaryColor?: string;
  agentPortalTitle?: string;
  agentPortalLogo?: string;
  agentPortalFavicon?: string;
  agentPortalAuthPageLogo?: string;
  [key: string]: any;
}

interface SettingsContextType {
  settings: Settings | null;
  loading: boolean;
  refreshSettings: () => Promise<void>;
  getPrimaryColor: () => string;
  getFavicon: () => string | null;
  getLogo: () => string | null;
  getAuthPageLogo: () => string | null;
  getTitle: () => string;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
  initialSettings?: Settings | null;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';
const SETTINGS_ENDPOINT = `${API_BASE_URL}/admin/settings`;

export const fetchSettingsFromApi = async (): Promise<Settings> => {
  const response = await axios.get(SETTINGS_ENDPOINT, {
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0',
    },
  });
  return response.data.data;
};

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children, initialSettings = null }) => {
  const [settings, setSettings] = useState<Settings | null>(initialSettings);
  const [loading, setLoading] = useState(!initialSettings);

  // Helper function to darken a color
  const darkenColor = (color: string, percent: number): string => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const newR = Math.max(0, Math.floor(r * (1 - percent / 100)));
    const newG = Math.max(0, Math.floor(g * (1 - percent / 100)));
    const newB = Math.max(0, Math.floor(b * (1 - percent / 100)));
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  };

  // Helper function to convert hex to RGB
  const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const applyPrimaryColor = (primaryColor?: string) => {
    if (!primaryColor) return;

    const root = document.documentElement;
    root.style.setProperty('--bs-primary', primaryColor);
    root.style.setProperty('--primary', primaryColor);

    const darkerColor = darkenColor(primaryColor, 20);
    root.style.setProperty('--primary-dark', darkerColor);
    root.style.setProperty('--bs-primary-dark', darkerColor);

    const rgb = hexToRgb(primaryColor);
    if (rgb) {
      root.style.setProperty('--bs-primary-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`);
    }
  };

  const fetchSettings = async () => {
    try {
      const data = await fetchSettingsFromApi();
      setSettings(normaliseBrandSettings(data));
      applyPrimaryColor(data.agentPortalPrimaryColor);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setSettings({
        agentPortalPrimaryColor: '#0C6AB2',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialSettings) {
      applyPrimaryColor(initialSettings.agentPortalPrimaryColor);
      setLoading(false);
      return;
    }

    fetchSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshSettings = async () => {
    await fetchSettings();
  };

  const getPrimaryColor = (): string => {
    return settings?.agentPortalPrimaryColor || '#0C6AB2';
  };

  const getFavicon = (): string | null => {
    // Use agent portal favicon only (no fallback to logo)
    if (settings?.agentPortalFavicon) {
      // Trim whitespace and check if it's not empty
      const faviconPath = typeof settings.agentPortalFavicon === 'string' 
        ? settings.agentPortalFavicon.trim() 
        : settings.agentPortalFavicon;
      
      if (faviconPath) {
        const url = getLogoUrl(faviconPath);
        // Only return if we got a valid URL (not null or empty string)
        if (url && url.trim()) {
          return url;
        }
      }
    }
    return '/scholar_vest_logo.png'; // Fallback to default favicon
  };

  const getLogo = (): string | null => {
    // Use agent portal logo only (no fallback to favicon)
    if (settings?.agentPortalLogo) {
      const url = getLogoUrl(settings.agentPortalLogo);
      if (url) return url;
    }
    return '/scholar_vest_logo_full.png'; // Fallback to default logo
  };

  const getTitle = (): string => {
    return normaliseBrandTitle(settings?.agentPortalTitle);
  };

  const getAuthPageLogo = (): string | null => {
    // Use agent portal auth page logo if available, otherwise fallback to logo or default
    if (settings?.agentPortalAuthPageLogo) {
      const url = getLogoUrl(settings.agentPortalAuthPageLogo);
      if (url) return url;
    }
    // Fallback to regular logo if auth page logo is not set
    return getLogo();
  };

  const value: SettingsContextType = {
    settings,
    loading,
    refreshSettings,
    getPrimaryColor,
    getFavicon,
    getLogo,
    getAuthPageLogo,
    getTitle,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
